/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { AllOption, MultiSelectProps } from 'react-crane'

import Select from '../Select'
import { DropdownMode } from '../Dropdown'

interface FilterMultiDropdownProps extends MultiSelectProps {
  /** Id of control */
  id: string
  /** Label for field */
  label: string
  /** List of options for the dropdown */
  options: object[]
  /** Indicates that label should be screen-ready only */
  srOnlyLabel?: boolean
}

type AllOptionsType = {
  [key: string]: string
} & AllOption

const selectWrapperCss = css`
  input {
    margin-right: 8px;
  }
`

const defaultProps: Pick<FilterMultiDropdownProps, 'srOnlyLabel'> = {
  srOnlyLabel: false
}

/** To ensure the label placement is correct, omit the placeholder */
const FilterMultiSelect = ({ id, label, options, value, ...props }: FilterMultiDropdownProps) => {
  const { allOption, allowSelectAll, inputValue, labelKey, valueKey } = { ...props }

  const defaultAllOption = {
    id: 'all',
    label: 'Select All',
    value: 'all'
  }

  // Filtering logic borrowed and adapted from react-crane
  const allOptionAlt: AllOptionsType = { ...(allOption || defaultAllOption) }

  const getOptionLabel = (option: any) => {
    if (!labelKey) {
      return ''
    }
    return option[labelKey]
  }

  const matchOption = (option: any) => {
    let optionLabel = getOptionLabel(option)
    optionLabel = optionLabel.toLowerCase()

    return optionLabel.indexOf(inputValue?.toLowerCase()) > -1
  }

  const filterOptions = () => {
    let searchText = inputValue || ''

    if (searchText === '') {
      return options
    }

    searchText = searchText.toLowerCase()

    let isGrouped = false

    const filteredOptions = options.filter((option: any) => {
      // Don't include the select all option in filtered results
      if (allowSelectAll && allOption && allOptionAlt[valueKey || ''] === option[valueKey || '']) {
        return false
      }

      if (Array.isArray(option.options)) {
        isGrouped = true
        return option.options.some((o: any) => matchOption(o))
      }

      return matchOption(option)
    })

    const displayableOptions = isGrouped
      ? filteredOptions.map((option: any) => {
          if (Array.isArray(option.options)) {
            isGrouped = true
            return {
              ...option,
              options: option.options.filter((o: any) => matchOption(o))
            }
          }
          return option
        })
      : filteredOptions

    return displayableOptions
  }

  const opts = inputValue ? filterOptions() : options

  return (
    <Select
      css={selectWrapperCss}
      id={id}
      isSearchable
      label={label}
      mode={DropdownMode.Multi}
      options={opts}
      showInput
      // set empty value as undefined to move the label to its "unshrunk" position
      value={value?.length ? value : undefined}
      {...props}
    />
  )
}

FilterMultiSelect.defaultProps = defaultProps

export default FilterMultiSelect
